/**
 * Learns where the room's noise floor sits and gates "is this frame speech?"
 * above it. Domain-free: the caller feeds frame energies (RMS in 0..1) and
 * asks whether one is loud enough to be activity.
 *
 * Model: most frames in a conversation are not speech, so a low percentile of
 * the recent history is the noise floor. Speech has to clear that floor by
 * `multiplier`. The result is clamped to [minThreshold, maxThreshold] so a
 * silent room cannot make the gate hair-trigger and a loud one cannot raise
 * it past ordinary speech.
 *
 * Why not a constant: a fixed threshold that works in a quiet room sits below
 * the noise floor of a busy office, where every frame reads as speech — the
 * endpoint detector then never closes an utterance and emits max-length
 * blocks of near-silence, which a transcription model answers with
 * hallucinated "(upbeat music)"-style tags.
 *
 * Nothing here reads a clock: one observation is one frame.
 */
export interface AdaptiveEnergyGateOptions {
  /** Gate before a floor is learned, and the floor it can never fall below. */
  minThreshold: number
  /** Ceiling, so a loud room cannot gate out ordinary speech. */
  maxThreshold?: number
  /** How far above the learned floor a frame must sit to count as activity. */
  multiplier?: number
  /** Frames of history the floor is estimated from. */
  historySize?: number
  minObservations?: number
  /** Percentile of the history taken as the floor. */
  floorPercentile?: number
}

export class AdaptiveEnergyGate {
  private readonly minThreshold: number
  private readonly maxThreshold: number
  private readonly multiplier: number
  private readonly historySize: number
  private readonly minObservations: number
  private readonly floorPercentile: number

  private history: number[] = []
  private current: number

  constructor(options: AdaptiveEnergyGateOptions) {
    this.minThreshold = options.minThreshold
    // Scalar RMS cannot distinguish equally loud stationary noise and speech.
    // Keep the ceiling below the capture controller's measured lower bound for
    // ordinary speech (~0.05), and let browser suppression handle louder noise.
    this.maxThreshold = options.maxThreshold ?? 0.04
    this.multiplier = options.multiplier ?? 2.5
    // ~4 s at the capture frame size (21 ms): long enough to span the pauses
    // between phrases, short enough to follow a room that changes.
    this.historySize = options.historySize ?? 200
    this.minObservations = options.minObservations ?? 25
    this.floorPercentile = options.floorPercentile ?? 0.2
    this.current = this.minThreshold
  }

  /** The energy a frame currently has to reach to count as activity. */
  get threshold(): number {
    return this.current
  }

  /** The learned noise floor, or null while the history is too short. */
  get noiseFloor(): number | null {
    if (this.history.length < this.minObservations) return null

    const sorted = [...this.history].sort((a, b) => a - b)
    return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * this.floorPercentile))]
  }

  /** Record a frame's energy and answer whether it reads as activity. */
  observe(energy: number): boolean {
    // Ask before learning: a frame is judged against the floor as it stood,
    // so the first loud frame of an utterance cannot raise the bar past
    // itself.
    const voiced = energy >= this.current

    this.history.push(energy)
    if (this.history.length > this.historySize) this.history.shift()

    const floor = this.noiseFloor
    if (floor !== null) {
      this.current = Math.min(this.maxThreshold, Math.max(this.minThreshold, floor * this.multiplier))
    }

    return voiced
  }

  /** Forget the learned floor (e.g. a new microphone). */
  reset() {
    this.history = []
    this.current = this.minThreshold
  }
}
